import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ClipEditorPatch } from '../api/clipEditor';

interface ClipEditorContextType {
  isOpen: boolean;
  openEditor: (clipId: string) => void;
  closeEditor: () => void;
  activeClipId: string | null;
  setActiveClipId: (id: string | null) => void;
  
  // For the active clip to listen to drafted patches from the global editor
  draftPatch: ClipEditorPatch | null;
  setDraftPatch: (patch: ClipEditorPatch | null) => void;
  
  // For the active clip to accept a patch
  acceptPatchTrigger: number;
  triggerAcceptPatch: () => void;
  
  // For the active clip to revert a patch
  revertPatchTrigger: number;
  triggerRevertPatch: () => void;
}

const ClipEditorContext = createContext<ClipEditorContextType | undefined>(undefined);

export const ClipEditorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeClipId, setActiveClipId] = useState<string | null>(null);
  
  const [draftPatch, setDraftPatch] = useState<ClipEditorPatch | null>(null);
  const [acceptPatchTrigger, setAcceptPatchTrigger] = useState(0);
  const [revertPatchTrigger, setRevertPatchTrigger] = useState(0);

  const openEditor = (clipId: string) => {
    setActiveClipId(clipId);
    setIsOpen(true);
  };

  const closeEditor = () => {
    setIsOpen(false);
    // optionally reset draft patches on close
  };

  const triggerAcceptPatch = () => {
    setAcceptPatchTrigger(prev => prev + 1);
  };
  
  const triggerRevertPatch = () => {
    setRevertPatchTrigger(prev => prev + 1);
    setDraftPatch(null);
  };

  return (
    <ClipEditorContext.Provider
      value={{
        isOpen,
        openEditor,
        closeEditor,
        activeClipId,
        setActiveClipId,
        draftPatch,
        setDraftPatch,
        acceptPatchTrigger,
        triggerAcceptPatch,
        revertPatchTrigger,
        triggerRevertPatch
      }}
    >
      {children}
    </ClipEditorContext.Provider>
  );
};

export const useClipEditorContext = () => {
  const context = useContext(ClipEditorContext);
  if (context === undefined) {
    throw new Error('useClipEditorContext must be used within a ClipEditorProvider');
  }
  return context;
};
